import axios, { AxiosError, AxiosRequestConfig } from "axios";

const ACCESS_KEY = "access";
const REFRESH_KEY = "refresh";
const REFRESH_URL = "/api/v1/auth/refresh-token";

export const getAccessToken = () => localStorage.getItem(ACCESS_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_KEY);

export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem("user");
};

// Best-effort local read of the access token's "exp" claim - no signature
// check needed, this only decides whether it's worth asking the server to
// refresh at all, the server is still the one that actually validates it.
const decodeJwtExpiry = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = JSON.parse(json)?.exp;
    return typeof exp === "number" ? exp : null;
  } catch {
    return null;
  }
};

// A little slack so callers refresh just before actual expiry instead of
// racing it - a request built with a token that expires mid-flight would
// still come back 401.
const EXPIRY_BUFFER_SECONDS = 30;

export const isAccessTokenExpired = (): boolean => {
  const token = getAccessToken();
  if (!token) return true;

  const exp = decodeJwtExpiry(token);
  if (exp == null) return true;

  return Date.now() / 1000 >= exp - EXPIRY_BUFFER_SECONDS;
};

export const axiosInstance = axios.create({
  baseURL: `${window["env"]["useSsl"] ? "https" : "http"}://${
    window["env"]["apiDomain"]
  }`,
  paramsSerializer: {
    indexes: null,
  },
});

// The backend rotates the refresh token on every use (see pl-back's
// SecurityService.refresh) - it's single-use, and using it also
// invalidates the access token issued alongside it (both are checked
// against the same server-side version counter). Multiple independent
// callers here (the response interceptor below, authProvider.checkAuth,
// authProvider.checkError) each used to fire their own /refresh-token
// call with whatever token was in localStorage at that moment; only the
// first would succeed, and the rest got "Invalid token" back from the
// refresh endpoint itself - which authProvider then treated as a dead
// session and force-logged-out the user, even though a sibling call had
// *just* written a valid new token pair. Sharing one in-flight promise
// means every caller awaits the same refresh instead of racing it.
let refreshPromise: Promise<string> | null = null;

export const refreshTokens = (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const token = getRefreshToken();
  if (!token) {
    return Promise.reject(new Error("No refresh token available"));
  }

  refreshPromise = axiosInstance
    .post(REFRESH_URL, { refreshToken: token })
    .then((response) => {
      setTokens(response.data.accessToken, response.data.refreshToken);
      return response.data.accessToken as string;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

axiosInstance.interceptors.request.use((request) => {
  const token = getAccessToken();
  if (token && request.headers) {
    request.headers["Authorization"] = "Bearer " + token;
  }

  return request;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string }>) => {
    const status = error.response?.status;
    const message = error.response?.data?.message;
    const config = error.config as
      | (AxiosRequestConfig & { _retried?: boolean })
      | undefined;

    const isAuthFailure =
      status === 401 &&
      (message === "Token is expired" || message === "Invalid token");

    // Never try to "refresh the refresh": a failing /refresh-token call
    // must propagate as-is so the caller (authProvider) can log the user
    // out, instead of retriggering another refresh attempt forever.
    const isRefreshCall = config?.url === REFRESH_URL;

    if (!isAuthFailure || isRefreshCall || !config || config._retried) {
      return Promise.reject(error);
    }

    config._retried = true;

    return refreshTokens().then((accessToken) => {
      if (config.headers) {
        config.headers["Authorization"] = "Bearer " + accessToken;
      }
      return axiosInstance.request(config);
    });
  }
);
